import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Phone, Lock, ArrowRight, ArrowLeft, MapPin, Calendar, Clock, Sparkles, Gem, ChefHat,
} from "lucide-react";
import { api } from "@/lib/api";
import { trackLead, trackEvent } from "@/lib/analytics";
import { normalizeIndianPhone, openWhatsApp } from "@/lib/lead-helpers";
import {
  LUXURY_DESTINATIONS,
  LUXURY_HONEYMOON_PHONE,
  LUXURY_HONEYMOON_PHONE_DISPLAY,
} from "@/config/honeymoon-experiences";

const WA_LINE = LUXURY_HONEYMOON_PHONE.replace(/\D/g, "");

export default function LuxuryHoneymoonDestination() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const conversionSent = useRef(false);

  const dest = LUXURY_DESTINATIONS.find((d) => d.slug === slug);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [dates, setDates] = useState("");
  const [vision, setVision] = useState("");
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => {
    if (dest) trackEvent("luxury_dest_view", { slug: dest.slug, name: dest.name });
  }, [dest]);

  if (!dest) {
    return (
      <div className="min-h-screen bg-[#f9f6f1] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-serif-vp text-4xl text-[#141210]">Destination not found</h1>
        <p className="text-[#5f574a] mt-3 max-w-md">The destination you're looking for isn't in our current curation. Explore our full collection instead.</p>
        <Link to="/honeymoon-experiences" className="mt-6 inline-flex items-center gap-2 bg-[#141210] text-[#e4c07c] rounded-full px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold" data-testid="lux-dest-back-404">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Experiences
        </Link>
      </div>
    );
  }

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const submit = async (e) => {
    e?.preventDefault();
    if (hp) return;
    const cleanPhone = normalizeIndianPhone(phone);
    if (!name.trim() || !cleanPhone) { toast.error("Please share your name and phone / WhatsApp number."); return; }
    if (cleanPhone.length !== 10) { toast.error("Enter a valid 10-digit Indian mobile number."); return; }
    setSubmitting(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: cleanPhone,
        destination: `Luxury Honeymoon · ${dest.name}`,
        preferences: [
          `Dates: ${dates || "flexible"}`,
          `Vision: ${vision || "not shared"}`,
        ].join(" · "),
        source: `lp_lux_dest_${dest.slug}`,
        utm_source: utm.get("utm_source") || null,
        utm_medium: utm.get("utm_medium") || null,
        utm_campaign: utm.get("utm_campaign") || "luxury_honeymoon",
        utm_term: utm.get("utm_term") || dest.slug,
        utm_content: utm.get("utm_content") || null,
      };
      const { data: lead } = await api.post("/leads", payload);
      if (!conversionSent.current) {
        conversionSent.current = true;
        trackLead({
          source: `lp_lux_dest_${dest.slug}_form`,
          destination: dest.name,
          value: 500000,
          label: "luxury_honeymoon_lead",
          extra: { lead_id: lead?.id || null, slug: dest.slug },
        });
        try {
          if (typeof window.fbq === "function") {
            window.fbq("trackCustom", "LuxuryHoneymoonLead", { destination: dest.name, slug: dest.slug, value: 500000, currency: "INR" });
          }
        } catch (err) { console.warn("fbq", err); }
      }
      const lines = [
        `Hi Vacations Pro — I'm interested in a bespoke ${dest.name} honeymoon.`,
        "",
        `Name: ${payload.name}`,
        `Phone / WhatsApp: +91 ${cleanPhone}`,
        `Email: ${payload.email || "—"}`,
        `Destination: ${dest.name} (${dest.country})`,
        `Travel dates: ${dates || "flexible"}`,
        vision ? `Vision: ${vision}` : "",
      ].filter(Boolean);
      openWhatsApp(WA_LINE, lines.join("\n"));
      toast.success("Received — WhatsApp is opening. A VIP concierge will call within 30 minutes.");
      setName(""); setPhone(""); setEmail(""); setDates(""); setVision("");
    } catch (err) {
      console.warn("Lux dest submit failed", err);
      toast.error(err?.response?.data?.detail || "Couldn't submit — please call our concierge directly.");
    } finally { setSubmitting(false); }
  };

  const trip = {
    "@context": "https://schema.org", "@type": "TouristTrip",
    "name": `Luxury Honeymoon · ${dest.name}`,
    "description": `Bespoke luxury honeymoon in ${dest.name}. ${dest.heroLine}. From ${dest.priceFrom}.`,
    "touristType": ["Honeymoon", "Luxury"],
    "provider": { "@type": "TravelAgency", "name": "Vacations Pro VIP", "telephone": LUXURY_HONEYMOON_PHONE },
    "itinerary": {
      "@type": "ItemList",
      "itemListElement": dest.signatureExperiences.map((x, i) => ({
        "@type": "ListItem", "position": i + 1,
        "item": { "@type": "TouristAttraction", "name": x.title, "description": x.body },
      })),
    },
    "image": [dest.hero],
  };

  return (
    <div className="min-h-screen bg-[#f9f6f1] text-[#141210]" data-testid={`lux-dest-page-${dest.slug}`}>
      <Helmet>
        <title>{`${dest.name} Luxury Honeymoon Experiences · ${dest.priceFrom} | Vacations Pro VIP`}</title>
        <meta name="description" content={`Bespoke ${dest.name} honeymoon curation for Indian HNW couples — ${dest.heroLine}. Private chauffeurs, chef dinners, 24/7 concierge. From ${dest.priceFrom}. Call ${LUXURY_HONEYMOON_PHONE_DISPLAY}.`} />
        <link rel="canonical" href={`https://vacationspro.com/honeymoon-experiences/${dest.slug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${dest.name} Luxury Honeymoon Experiences | Vacations Pro VIP`} />
        <meta property="og:description" content={`${dest.heroLine}. Curated for Indian HNW couples. From ${dest.priceFrom}.`} />
        <meta property="og:url" content={`https://vacationspro.com/honeymoon-experiences/${dest.slug}`} />
        <meta property="og:image" content={dest.hero} />
        <meta property="og:site_name" content="Vacations Pro" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${dest.name} Luxury Honeymoon Experiences | Vacations Pro VIP`} />
        <meta name="twitter:description" content={`${dest.heroLine}. From ${dest.priceFrom}.`} />
        <meta name="twitter:image" content={dest.hero} />
        <script type="application/ld+json">{JSON.stringify(trip)}</script>
      </Helmet>

      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-[#f9f6f1]/95 backdrop-blur border-b border-[#e6dcc9] px-4 md:px-10 py-2.5 md:py-3 flex items-center justify-between gap-3">
        <button onClick={() => navigate("/honeymoon-experiences")} className="text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-[#5f574a] hover:text-[#141210] inline-flex items-center gap-1.5 md:gap-2 shrink-0" data-testid="lux-dest-back">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">All Experiences</span>
          <span className="sm:hidden">Back</span>
        </button>
        <a
          href={`tel:${LUXURY_HONEYMOON_PHONE}`}
          onClick={() => trackLead({ source: `lp_lux_dest_${dest.slug}_header_call`, destination: dest.name, label: "luxury_honeymoon_lead" })}
          className="inline-flex items-center gap-1.5 md:gap-2 bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full px-3.5 md:px-5 py-2 md:py-2.5 text-[11px] md:text-xs tracking-[0.14em] uppercase font-semibold shadow-sm transition-colors whitespace-nowrap"
          data-testid={`lux-dest-${dest.slug}-header-call`}
          aria-label={`Call VIP concierge ${LUXURY_HONEYMOON_PHONE_DISPLAY}`}
        >
          <Phone className="w-3.5 h-3.5 shrink-0" />
          <span className="hidden md:inline">Call VIP · {LUXURY_HONEYMOON_PHONE_DISPLAY}</span>
          <span className="md:hidden">Call VIP</span>
        </a>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={dest.hero} alt={`${dest.name} luxury honeymoon`}
               className="w-full h-full object-cover animate-[destKen_22s_ease-in-out_infinite_alternate]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/40 to-black/85" />
        </div>
        <style>{`@keyframes destKen { 0%{transform:scale(1.02) translateY(0);} 100%{transform:scale(1.09) translateY(-1.2%);} }`}</style>

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12 pt-14 md:pt-24 pb-14 md:pb-20 text-white">
          <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/25 rounded-full px-3 py-1 text-[11px] tracking-[0.25em] uppercase font-semibold">
            <Gem className="w-3.5 h-3.5 text-[#e4c07c]" /> {dest.country} · VIP Curation
          </div>
          <h1 className="font-serif-vp text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6 drop-shadow-[0_4px_28px_rgba(0,0,0,0.55)]">
            {dest.name}
            <span className="block text-[#e4c07c] italic text-3xl sm:text-4xl lg:text-5xl mt-3">{dest.tagline}</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed max-w-[640px] font-serif-vp">
            {dest.idealFor}
          </p>
          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <a
              href={`tel:${LUXURY_HONEYMOON_PHONE}`}
              onClick={() => trackLead({ source: `lp_lux_dest_${dest.slug}_hero_call`, destination: dest.name, label: "luxury_honeymoon_lead" })}
              className="inline-flex items-center gap-2 bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full px-7 py-4 text-[13px] tracking-[0.16em] uppercase font-bold transition-all hover:-translate-y-0.5"
              data-testid={`lux-dest-${dest.slug}-hero-call`}
            >
              <Phone className="w-4 h-4" /> Call {LUXURY_HONEYMOON_PHONE_DISPLAY}
            </a>
            <button onClick={scrollToForm}
                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 text-white rounded-full px-7 py-4 text-[13px] tracking-[0.16em] uppercase font-bold transition-colors"
                    data-testid={`lux-dest-${dest.slug}-hero-form`}>
              Curate My {dest.name} Journey <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-[560px] text-white/90">
            <div className="bg-white/[0.08] backdrop-blur border border-white/15 rounded-lg px-3 py-3">
              <Calendar className="w-4 h-4 text-[#e4c07c] mb-1" strokeWidth={1.5} />
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">Ideal Length</div>
              <div className="font-serif-vp text-base mt-0.5">{dest.duration}</div>
            </div>
            <div className="bg-white/[0.08] backdrop-blur border border-white/15 rounded-lg px-3 py-3">
              <Sparkles className="w-4 h-4 text-[#e4c07c] mb-1" strokeWidth={1.5} />
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">Best Season</div>
              <div className="font-serif-vp text-sm mt-0.5 leading-tight">{dest.bestSeason}</div>
            </div>
            <div className="bg-white/[0.08] backdrop-blur border border-white/15 rounded-lg px-3 py-3">
              <Gem className="w-4 h-4 text-[#e4c07c] mb-1" strokeWidth={1.5} />
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">From</div>
              <div className="font-serif-vp text-base mt-0.5">{dest.priceFrom}</div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE EXPERIENCES */}
      <section className="px-6 md:px-12 lg:px-20 py-20 max-w-[1200px] mx-auto" data-testid={`lux-dest-${dest.slug}-experiences`}>
        <div className="max-w-3xl">
          <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Signature Experiences</span>
          <h2 className="font-serif-vp text-4xl md:text-5xl leading-tight text-[#141210] mt-3">
            The four moments we plan every {dest.name} honeymoon around.
          </h2>
          <p className="text-sm text-[#5f574a] mt-4">
            Every experience below is arranged privately, off-hours, and without another couple in sight.
            None of it is on a group brochure. All of it is on your itinerary — if you want it.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {dest.signatureExperiences.map((x, i) => (
            <div key={x.title} className="bg-white border border-[#e6dcc9] rounded-2xl p-6 md:p-7 shadow-[0_6px_20px_rgba(20,18,16,0.06)]">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#a8874b] font-semibold">
                <span>Signature 0{i + 1}</span>
              </div>
              <h3 className="font-serif-vp text-2xl leading-tight text-[#141210] mt-3">{x.title}</h3>
              <p className="text-sm text-[#5f574a] leading-relaxed mt-3">{x.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STAY IDEAS */}
      <section className="bg-white border-y border-[#e6dcc9] px-6 md:px-12 lg:px-20 py-20" data-testid={`lux-dest-${dest.slug}-stays`}>
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl">
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Where You&apos;ll Wake Up</span>
            <h2 className="font-serif-vp text-4xl md:text-5xl leading-tight text-[#141210] mt-3">
              Three hotels our curators pick in {dest.name}.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {dest.stayIdeas.map((s, i) => (
              <div key={s.name} className="bg-[#faf7f1] border border-[#e6dcc9] rounded-2xl p-6">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#a8874b] font-semibold">Curator&apos;s pick 0{i + 1}</div>
                <h3 className="font-serif-vp text-xl leading-tight text-[#141210] mt-2">{s.name}</h3>
                <p className="text-xs text-[#5f574a] leading-relaxed mt-2">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="px-6 md:px-12 lg:px-20 py-20 max-w-[1100px] mx-auto grid md:grid-cols-[0.85fr_1.15fr] gap-10 items-start">
        <div>
          <span className="text-[11px] tracking-[0.32em] uppercase text-[#a8874b] font-semibold">Curate My Journey</span>
          <h2 className="font-serif-vp text-4xl leading-tight text-[#141210] mt-3">
            Let&apos;s plan your {dest.name} honeymoon.
          </h2>
          <p className="text-[15px] text-[#5f574a] mt-5 leading-relaxed">
            Every {dest.name} itinerary is built entirely around <em>your</em> pace — the pace of two people
            starting a life together. Share a few details and a senior curator calls within 30 minutes.
          </p>
          <div className="mt-6 rounded-xl border border-[#e6dcc9] bg-[#faf7f1] p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#a8874b] font-semibold">Prefer to talk?</div>
            <a href={`tel:${LUXURY_HONEYMOON_PHONE}`}
               className="mt-2 font-serif-vp text-2xl md:text-3xl text-[#141210] hover:text-[#a8874b] transition-colors inline-flex items-center gap-2"
               data-testid={`lux-dest-${dest.slug}-side-call`}>
              <Phone className="w-5 h-5" /> {LUXURY_HONEYMOON_PHONE_DISPLAY}
            </a>
            <div className="text-[11px] text-[#7a6f5f] mt-1">Direct to our VIP concierge · Mon–Sun · 9am–11pm IST</div>
          </div>
        </div>

        <form ref={formRef} onSubmit={submit}
              className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(20,18,16,0.12)] border border-[#e6dcc9] p-6 md:p-8 space-y-3"
              data-testid={`lux-dest-${dest.slug}-form`}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#a8874b] font-semibold">Request {dest.name} Itinerary</div>
          <h3 className="font-serif-vp text-2xl md:text-3xl text-[#141210] leading-snug">
            Every detail. Yours to shape.
          </h3>

          <input type="text" name="hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
                 value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" />

          <FormInput label="Full Name" value={name} onChange={setName} required testid={`lux-dest-${dest.slug}-name`} />
          <FormInput label="Phone / WhatsApp (required)" type="tel" required testid={`lux-dest-${dest.slug}-phone`}
                     value={phone} onChange={(v) => setPhone(normalizeIndianPhone(v))} />
          <FormInput label="Email Address" type="email" value={email} onChange={setEmail} testid={`lux-dest-${dest.slug}-email`} />
          <FormInput label="Estimated Travel Dates" value={dates} onChange={setDates}
                     placeholder="e.g. 12–20 April 2026, or flexible" testid={`lux-dest-${dest.slug}-dates`} />
          <label className="block border border-[#e0d3b9] rounded-lg px-3 py-2 bg-white focus-within:border-[#a8874b] transition-colors">
            <div className="text-[10px] tracking-wider uppercase text-[#a8874b] font-semibold">Tell us what kind of experience you are dreaming of…</div>
            <textarea value={vision} onChange={(e) => setVision(e.target.value)} rows={3}
                      placeholder={dest.signatureExperiences[0]?.title + " · " + dest.signatureExperiences[1]?.title + "…"}
                      className="w-full bg-transparent outline-none text-[15px] mt-0.5 resize-none placeholder:text-[#a8977a]"
                      data-testid={`lux-dest-${dest.slug}-vision`} />
          </label>

          <button type="submit" disabled={submitting}
                  className="w-full bg-[#141210] hover:bg-[#0a0908] text-[#e4c07c] rounded-xl py-4 text-[13px] tracking-[0.18em] uppercase font-bold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 mt-2"
                  data-testid={`lux-dest-${dest.slug}-submit`}>
            {submitting ? "Sending…" : "Request Custom Itinerary"} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-[#7a6f5f] text-center inline-flex items-center justify-center gap-1.5 w-full">
            <Lock className="w-3 h-3" /> Preview itinerary within 24 hours · Encrypted & confidential
          </p>
        </form>
      </section>

      <a href={`tel:${LUXURY_HONEYMOON_PHONE}`}
         onClick={() => trackLead({ source: `lp_lux_dest_${dest.slug}_float_call`, destination: dest.name, label: "luxury_honeymoon_lead" })}
         className="fixed bottom-6 right-5 z-40 w-14 h-14 flex items-center justify-center bg-[#a8874b] hover:bg-[#96763d] text-white rounded-full shadow-[0_14px_35px_rgba(168,135,75,0.45)] transition-all hover:-translate-y-0.5"
         aria-label={`Call VIP concierge for ${dest.name}`}
         data-testid={`lux-dest-${dest.slug}-float-call`}>
        <Phone className="w-6 h-6" />
      </a>

      <footer className="px-6 md:px-12 py-8 text-center text-[11px] text-[#7a6f5f] flex items-center justify-center gap-2">
        <MapPin className="w-3.5 h-3.5" />
        © {new Date().getFullYear()} Vacations Pro VIP · Ludhiana · Delhi · Mumbai
      </footer>
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", placeholder = "", required = false, testid }) {
  return (
    <label className="block border border-[#e0d3b9] rounded-lg px-3 py-2 bg-white focus-within:border-[#a8874b] transition-colors">
      <div className="text-[10px] tracking-wider uppercase text-[#a8874b] font-semibold">{label}</div>
      <input type={type} value={value} required={required} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             className="w-full bg-transparent outline-none text-[15px] mt-0.5 placeholder:text-[#a8977a]"
             data-testid={testid} />
    </label>
  );
}
